import { Injectable } from '@angular/core';

import { ResourceItem } from '../interface/resources.model';
import { resourcesDocs, resourcesList } from '../data/resources.data';
import { slugify } from '../utils/resource-filter.util';

@Injectable({
  providedIn: 'root',
})
export class ResourcesService {
  private readonly resources = resourcesDocs;
  private readonly resourceList = resourcesList;

  private readonly resourceMap = new Map<string, ResourceItem>();
  private readonly resourceIdMap = new Map<number, ResourceItem>();
  private readonly resourcesByType = new Map<string, ResourceItem[]>();

  private readonly topicSlugMap = new Map<string, string>();
  private readonly validTypes = new Set<string>();

  private static readonly DEFAULT_ICON = '/assets/img/thumbnails/filters/all.svg';

  private readonly resourceTypes = new Map<string, { type: string; name: string }>();

  private readonly resourceListMap = new Map<string, (typeof resourcesList)[number]>();

  constructor() {
    for (const resource of this.resources) {
      this.resourceMap.set(this.createResourceKey(resource.type, resource.slug), resource);

      this.resourceIdMap.set(resource.id, resource);

      this.validTypes.add(resource.type);

      if (!this.resourceTypes.has(resource.type)) {
        this.resourceTypes.set(resource.type, {
          type: resource.type,
          name: resource.name,
        });
      }

      const items = this.resourcesByType.get(resource.type) ?? [];
      items.push(resource);
      this.resourcesByType.set(resource.type, items);

      for (const topic of resource.topics) {
        this.topicSlugMap.set(slugify(topic), topic);
      }
    }

    for (const item of this.resourceList) {
      this.resourceListMap.set(item.slug, item);
    }
  }

  private createResourceKey(type: string, slug: string): string {
    return `${type}:${slug}`;
  }

  getAllResources(): ResourceItem[] {
    return this.resources;
  }

  getResource(type: string, slug: string): ResourceItem | undefined {
    return this.resourceMap.get(this.createResourceKey(type, slug));
  }

  getResourceById(id: number): ResourceItem | undefined {
    return this.resources.find((r) => r.id === id);
  }

  getResourcesByType(type?: string): readonly ResourceItem[] {
    if (!type) {
      return this.resources;
    }

    return this.resourcesByType.get(type) ?? [];
  }

  getPreSelectedResources(): ResourceItem[] {
    return this.resources.filter((resource) => resource.preSelected);
  }

  getRelatedResources(resource: ResourceItem): ResourceItem[] {
    return this.resources
      .filter((item) => item.id !== resource.id && item.type === resource.type)
      .slice(0, 3);
  }

  isValidType(type: string): boolean {
    return this.validTypes.has(type);
  }

  getTypes(): { type: string; name: string }[] {
    return [...this.resourceTypes.values()];
  }

  getResourceType(type: string) {
    return this.resourceListMap.get(type);
  }

  getTypeIcon(type: string): string {
    return this.resourceListMap.get(type)?.logo ?? ResourcesService.DEFAULT_ICON;
  }

  getResourcesList() {
    return this.resourceList;
  }

  isValidTopic(topicSlug: string): boolean {
    return this.topicSlugMap.has(topicSlug);
  }

  getTopicFromSlug(topicSlug: string | null): string | undefined {
    if (!topicSlug) {
      return undefined;
    }

    return this.topicSlugMap.get(topicSlug);
  }

  getTopics(): string[] {
    return [...this.topicSlugMap.values()];
  }
}
